import { lazy, Suspense, useState } from "react";
import { Loader2 } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatsWidgets } from "@/components/StatsWidgets";
import { CrawlProgress } from "@/components/CrawlProgress";
import { DomainsTable } from "@/components/DomainsTable";
import { CSVUpload } from "@/components/CSVUpload";
import { useAppContext } from "@/lib/AppContext";

// Other tabs load on first use, so the domain list (the start page) ships less JavaScript.
const AnalyticsPage = lazy(() => import("@/pages/AnalyticsPage"));
const CrawlerPage = lazy(() => import("@/pages/CrawlerPage"));
const DeepResearchPage = lazy(() => import("@/pages/DeepResearchPage"));
const JobHistoryPage = lazy(() => import("@/pages/JobHistoryPage"));
const TokenUsagePage = lazy(() => import("@/pages/TokenUsagePage"));
const UsersPage = lazy(() => import("@/pages/UsersPage"));
const AuditPage = lazy(() => import("@/pages/AuditPage"));

const TabFallback = () => (
  <div className="p-6 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /></div>
);

export default function DashboardPage({ user, onLogout }) {
  const { stats, refreshNow } = useAppContext();
  const [refreshKey, setRefreshKey] = useState(0);
  const [activeTab, setActiveTab] = useState("domains");

  const onUploadComplete = () => {
    refreshNow();
    setRefreshKey((k) => k + 1);
  };

  return (
    <DashboardLayout onLogout={onLogout} activeTab={activeTab} onTabChange={setActiveTab} currentUser={user}>
      {activeTab === "domains" && (
        <div className="space-y-4 sm:space-y-6 p-3 sm:p-6" data-testid="dashboard-content">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-2xl sm:text-4xl font-black tracking-tight text-foreground">
                Dashboard
              </h1>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                CMS-Erkennung — Drupal-Versionen — Lead Intelligence
                {stats ? ` — ${stats.total.toLocaleString()} domains` : ""}
              </p>
            </div>
            {user.role !== "viewer" && <CSVUpload onUploadComplete={onUploadComplete} />}
          </div>
          {stats && <StatsWidgets stats={stats} />}
          {stats && stats.total > 0 && (
            <CrawlProgress stats={stats} onRefresh={refreshNow} />
          )}
          <DomainsTable refreshKey={refreshKey} onRefresh={refreshNow} role={user.role} currentUser={user} />
        </div>
      )}
      <Suspense fallback={<TabFallback />}>
      {activeTab === "analytics"  && <AnalyticsPage />}
      {activeTab === "crawler"    && <CrawlerPage role={user.role} />}
      {activeTab === "research"   && <DeepResearchPage />}
      {activeTab === "jobs"       && <JobHistoryPage />}
      {activeTab === "tokens"     && <TokenUsagePage />}
      {activeTab === "users" && user.role === "admin" && <UsersPage currentUser={user} />}
      {activeTab === "audit" && user.role === "admin" && <AuditPage />}
      </Suspense>
    </DashboardLayout>
  );
}
